import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E3F2FD] to-white flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full text-center">
        {/* Visual Element */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-10 relative"
        >
          <div className="w-32 h-32 mx-auto rounded-full bg-[#FFF0F3] flex items-center justify-center">
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            >
              <span className="text-6xl">📚</span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -bottom-4 -right-4 text-3xl"
          >
            ❓
          </motion.div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h1 className="text-6xl font-bold text-[#5E60CE] mb-4 font-display">Oops!</h1>
          <h2 className="text-3xl font-semibold text-[#4F86F7] mb-6 font-display">Page Not Found</h2>
          <p className="text-xl text-gray-600 mb-10 font-display">
            We can't seem to find the page you're looking for. Let's find a better story!
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="space-x-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-6 py-3 border-2 border-[#C7CEEA] rounded-full text-md font-medium text-[#5E60CE] bg-white hover:bg-[#F0F7FF] transition-all duration-300 shadow-md font-display"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/app')}
            className="inline-flex items-center px-6 py-3 border-2 border-transparent rounded-full text-md font-medium text-white bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] hover:shadow-lg transition-all duration-300 shadow-md font-display"
          >
            <Home className="mr-2 h-4 w-4" />
            Back to Stories
          </motion.button>
        </motion.div>

        {/* Fun illustration */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-12 max-w-xs mx-auto"
        >
          <div className="relative">
            <div className="w-full h-1 bg-[#E2F0CB] absolute bottom-0 left-0 rounded-full"></div>
            <div className="flex justify-center">
              <motion.div
                animate={{ x: [0, -10, 10, -5, 5, 0] }}
                transition={{ duration: 4, repeat: Infinity, repeatDelay: 1 }}
              >
                <span className="text-5xl">🦙</span>
              </motion.div>
              <div className="mx-3 text-gray-400 font-display text-sm text-center mt-auto mb-2">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                >
                  searching...
                </motion.div>
              </div>
              <span className="text-4xl">🔍</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound; 